import * as Yup from "yup";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
// form
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
// @mui
import { Stack, IconButton, InputAdornment, Typography, Box, Alert } from "@mui/material";
import { LoadingButton } from "@mui/lab";
// components
import Iconify from "../../../components/Iconify";
import { RHFTextField } from "../../../components/hook-form";
import { hotelUserRegisteration } from "../../../api-config/config";

// Add a typed model that matches your form fields
interface RegisterFormValues {
  hotel_name: string;
  first_name: string;
  last_name: string;
  email_id: string;
  mobile_number: string;
  password: string;
  noOfRoom?: string;
  country?: string;
  state?: string;
}

export default function SimpleRegisterForm() {
  const navigate = useNavigate();
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [numberFieldWarning, setNumberFieldWarning] = useState({ field: "", message: "" });
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const RegisterSchema = Yup.object().shape({
    hotel_name: Yup.string()
      .matches(/^[A-Za-z0-9À-ž\s'&-.]+$/, "Invalid hotel name")
      .required("Hotel name required"),
    mobile_number: Yup.string()
      .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits")
      .required("Mobile No required"),
    email_id: Yup.string()
      .email("Email must be a valid email address")
      .required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  // Ensure useForm is typed
  const methods = useForm<RegisterFormValues>({
    resolver: yupResolver(RegisterSchema),
    defaultValues: {
      hotel_name: "",
      first_name: "",
      last_name: "",
      email_id: "",
      mobile_number: "",
      password: "",
      noOfRoom: "",
      country: "",
      state: "",
    },
  });

  const {
    handleSubmit, clearErrors
  } = methods;

  // onSubmit typed to the same model
  const onSubmit: SubmitHandler<RegisterFormValues> = async (formValue) => {
    console.log(formValue);
    const { hotel_name, email_id, mobile_number, password } = formValue;
    setIsSubmitting(true);
    setError(false);
    setErrorMessage("");
    const frontend_url = window.location.origin;
    hotelUserRegisteration("", "", hotel_name, mobile_number, email_id, password, "", "", 0, frontend_url).then(
      (res: any) => {
        if (res.status_message === "success") {
          setTimeout(() => {
            setIsSubmitting(false);
            setRegistrationSuccess(true); // <-- Show success message
            // navigate("/main/home", { replace: true }); // Remove this
          }, 500);
        } else {
          // backend returns { status_message: "failed", message: "x already exist" }
          setIsSubmitting(false);
          setError(true);
          setErrorMessage(res.message || "Registration failed. Please try again.");
        }
      },
      (error: any) => {
        setIsSubmitting(false);
        let resMessage =
          (error.response &&
            error.response.data &&
            (error.response.data.message || error.response.data.msg)) ||
          error.message ||
          error.toString();

        if (resMessage === "Network Error") {
          resMessage = "Network Error. Please check your connection.";
        } else if (error.response && error.response.status === 422) {
          resMessage = error.response.data.msg || resMessage;
        }

        setError(true);
        setErrorMessage(resMessage);
        console.log("error", resMessage);
      }
    );
  };

  // Auto-dismiss warning after 3 seconds
  useEffect(() => {
    if (numberFieldWarning.field) {
      const timer = setTimeout(() => {
        setNumberFieldWarning({ field: "", message: "" });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [numberFieldWarning.field]);

  // Dismiss warning on any click
  useEffect(() => {
    const handleClick = () => {
      if (numberFieldWarning.field) {
        setNumberFieldWarning({ field: "", message: "" });
      }
      clearErrors();
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [numberFieldWarning.field, clearErrors]);

  if (registrationSuccess) {
    return (
      <Box sx={{ textAlign: "center" }}>
        <Box
          sx={{
            maxWidth: 400,
            mx: "auto",
            p: 4,
            borderRadius: 3,
            background: "white",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* <Iconify icon="mdi:check-circle-outline" sx={{ fontSize: 64, color: "success.main", mb: 1 }} /> */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: "green" }}>
            Registration Successful!
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", textAlign: "center", mb: 2 }}>
            Account created successfully. A verification link has been sent to your email and is valid for 24 hours. Please verify your email before logging in.
          </Typography>
          <Box
            component="a"
            href="/login"
            className="bg-sky-600 hover:bg-sky-400"
            sx={{
              // mt: 2,
              px: 5,
              py: 1,
              borderRadius: 1,
              color: "#fff",
              fontWeight: 400,
              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
              textDecoration: "none",
              transition: "background 0.3s",
              "&:hover": {
                textDecoration: "none",
              },
            }}
          >
            Go to Login
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <RHFTextField
            name="hotel_name"
            label="Hotel Name"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <Iconify icon={"ri:hotel-line"} sx={{}} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            other={{
              onInput: (e: React.ChangeEvent<HTMLInputElement>) => { 
                const original = e.target.value;
                const filtered = original.replace(/[^A-Za-z0-9À-ž\s'&-.]/g, '');
                if (original !== filtered) {
                  setNumberFieldWarning({ field: "hotel_name", message: "Some special characters are not  allowed!!!" });
                } else {
                  setNumberFieldWarning({ field: "", message: "" });
                }
                e.target.value = filtered;
              },
            }}
          />
          {numberFieldWarning.field === "hotel_name" && (
            <Typography variant="caption" sx={{ color: "error.main", ml: 2 }}>
              {numberFieldWarning.message}
            </Typography>
          )}
          
          <div>
            <RHFTextField
              name="mobile_number"
              label="Mobile Number"
              type="tel"
              other={{
                onInput: (e: React.ChangeEvent<HTMLInputElement>) => { 
                  const original = e.target.value;
                  const filtered = original.replace(/[^0-9]/g, '').slice(0, 10);
                  if (original !== filtered) {
                    setNumberFieldWarning({ field: "mobile_number", message: "Only numbers allowed! and max 10 digits" });
                  } else {
                    setNumberFieldWarning({ field: "", message: "" });
                  }
                  e.target.value = filtered;
                },
              }}
              InputProps={{
                inputProps: { maxLength: 10, pattern: "[0-9]*" },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end">
                      <Iconify icon={"material-symbols:phone-android"} sx={{}} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {numberFieldWarning.field === "mobile_number" && (
              <Typography variant="caption" sx={{ color: "error.main", ml: 2 }}>
                {numberFieldWarning.message}
              </Typography>
            )}
          </div>

          <RHFTextField
            name="email_id"
            label="Email address"
            other={{}}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <Iconify icon={"ic:outline-email"} sx={{}} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <RHFTextField
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <Iconify
                      icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                      sx={{}}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            other={{}}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            className="bg-sky-600 hover:bg-sky-400 text-stone-50"
            sx={{ background: "#0323f8" }}
          >
            Sign Up
          </LoadingButton>
        </Stack>
      </form>
    </FormProvider>
  );
}